import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { buildAbilityFromRules, confirmationDenialReason } from '@/hooks/useAbility';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { authStateResolvedAtom, userIdAtom } from '@/services/globalState';
import { isPendingOAuthUserId, shouldFetchUserScoped } from '@/util/authTokens';

/**
 * A centralized hook to manage and derive the user's authentication status.
 *
 * This hook serves as the single source of truth for determining whether a user is
 * fully authenticated, in a pending state (e.g., OAuth sign-up awaiting NPI
 * verification), or unauthenticated. It simplifies auth logic across the app
 * by providing clear, derived boolean states, reducing code duplication and
 * ensuring consistency.
 *
 * Returns an object with the following properties:
 *  - `userId`: The user's ID (numeric for authenticated, UUID for pending, null for guest).
 *    See userIdAtom docs for details on ID types.
 *  - `currentUser`: The full user object from the API for authenticated users.
 *  - `isAuthenticated`: `true` only if the user has a database record (numeric userId).
 *  - `isPending`: `true` if the user is a pending OAuth user (UUID userId).
 *  - `isUnauthenticated`: `true` if there is no userId.
 *  - `needsNpiVerification`: `true` for pending users, and for restricted users (see below)
 *    who do not yet hold a valid NPI. Also `true` while an already-submitted NPI is held for
 *    review — this only decides whether the user should be parked on the NPI screen, not
 *    what that screen renders.
 *  - `needsAccountDetailsConfirmation`: `true` for restricted users who already hold a valid NPI.
 *  - `isLoading`: `true` while the current user query is fetching for a user with a database
 *    record. Never `true` for a pending user: this hook's observer is disabled for them, but
 *    React Query reports the shared query's status, so another observer's fetch would otherwise
 *    read as loading here and blank the secure layout mid-fetch.
 *  - `isAuthResolved`: `true` once the cold-load session restore has settled.
 *
 * Whether an account is restricted at all comes from the server's CASL rules, not from the
 * NPI fields: the account may not submit an inquiry, and the denying rule names a
 * confirmation reason. Staff accounts hold no NPI and are never restricted, so the app does
 * not need to know they exist. `validNpi` only picks which of the two screens a restricted
 * account goes to, because the server's reason does not tell them apart.
 *
 * `isUnauthenticated` deliberately does not wait for `isAuthResolved`. It answers "is
 * there a user right now", which is what a screen choosing between a public and a signed-in
 * variant wants, and making it wait would flash the signed-in variant at a signed-out
 * visitor instead. Anything that *navigates* on the answer wants `isAuthResolved` as well,
 * because "still restoring" and "signed out" are the same value until it flips — a
 * distinction currently masked by the root layout holding the splash, and one that would
 * silently stop being masked if that gate ever moved.
 */
export function useAuthStatus() {
  const userId = useAtomValue(userIdAtom);

  const isAuthResolved = useAtomValue(authStateResolvedAtom);

  const { data: currentUser, isLoading: isCurrentUserLoading } = useQuery({
    ...getCurrentUserV2QueryOptions(userId),
    enabled: shouldFetchUserScoped(userId),
  });

  const isPending = isPendingOAuthUserId(userId);

  const isAuthenticated = shouldFetchUserScoped(userId);

  const isUnauthenticated = !userId;

  const isLoading = isAuthenticated && isCurrentUserLoading;

  const ability = useMemo(() => buildAbilityFromRules(currentUser?.rules), [currentUser?.rules]);

  const isConfirmationRestricted =
    isAuthenticated && currentUser !== undefined && confirmationDenialReason(ability) !== undefined;

  const needsNpiVerification =
    isPending || (isConfirmationRestricted && currentUser?.validNpi !== true);

  const needsAccountDetailsConfirmation =
    isConfirmationRestricted && currentUser?.validNpi === true;

  return {
    userId,
    currentUser,
    isAuthenticated,
    isPending,
    isUnauthenticated,
    needsNpiVerification,
    needsAccountDetailsConfirmation,
    isLoading,
    isAuthResolved,
  };
}
